import { HttpService } from './http.service';
import type { ShownConsent } from '@/features/legal/data/shownConsent';

export type PaidPlanId = 'COACH' | 'PRO' | 'CLUB';

export interface PlanEntitlements {
  /** Nombre maximal d'équipes du club — null = illimité. */
  maxTeams: number | null;
  /** Quota global de matchs (live + imports .dvw confondus) — null = illimité. */
  maxMatches: number | null;
  dvwImport: boolean;
  scouting: boolean;
}

export interface SubscriptionView {
  plan: 'STARTER' | PaidPlanId;
  planStatus: string | null;
  /** ISO date — fin de la période en cours (renouvellement ou expiration). */
  planCurrentPeriodEnd: string | null;
  /** Teams paid on the account's own subscription (null on Starter). Optional during a deploy. */
  subscriptionQuantity?: number | null;
  /**
   * Club plan that gave the account no club of its own: a player account, a
   * member of a club someone else owns, or a staff payer with no club at all
   * (VOL-199). Optional during a deploy.
   */
  clubPlanBlocker?: 'role-not-eligible' | 'club-owned-by-other' | 'no-club' | null;
  entitlements: PlanEntitlements;
  usage: { matches: number; teams: number };
  /** false pendant l'accès anticipé : les limites sont mesurées mais pas appliquées. */
  limitsEnforced: boolean;
  /**
   * Best plan the user benefits from (own, club owner's, or a team payer's),
   * while `plan` is the account's own subscription (VOL-319). Optional during a deploy.
   */
  effectivePlan?: 'STARTER' | PaidPlanId;
  /** Paying club's name, only when the user is not the payer. */
  payerClubName?: string | null;
  /** Paying account's name, for staff who are not the payer. */
  payerOwnerName?: string | null;
  /** Stripe status of the payer's subscription (e.g. `past_due`), for staff who are not the payer. */
  payerPlanStatus?: string | null;
  /**
   * Covered by someone else's plan, the user also owns teams outside any club:
   * his own plan for them, with its quota and usage. Null otherwise.
   */
  ownTeams?: { plan: 'STARTER' | PaidPlanId; maxMatches: number | null; matches: number } | null;
  /**
   * Active-team choice when over quota (VOL-192). Optional while an older
   * backend may still answer during a deploy.
   */
  activeSelection?: {
    /** Unlocked teams in the quota scope. */
    activeTeams: number;
    /** Only the club owner or a club admin picks the active teams. */
    canChoose: boolean;
    /** Club owner's name, given to members who are not the owner. */
    ownerName: string | null;
    /** The quota above is the viewer's own plan; false when a club owner pays for it. */
    viewerIsPayer?: boolean;
  };
}

/**
 * `GET /payments/match-quota`: quota of the account that pays for a new match on
 * the team (VOL-199), so an invited coach is not shown his own free quota.
 */
export interface MatchQuotaView {
  limitsEnforced: boolean;
  /** Null when unlimited. */
  maxMatches: number | null;
  used: number;
  /** False when someone else's plan holds the quota: only the payer can upgrade it. */
  viewerIsPayer: boolean;
  /** Payer's name, for staff who are not the payer. */
  payerName: string | null;
}

export type VideoPackSize = 25 | 50 | 75;

export interface VideoPackPurchase {
  id: string;
  matches: number;
  amountCents: number;
  currency: string;
  createdAt: string;
}

/** `GET /payments/video` (VOL-332): the balance the viewer spends from is the payer's. */
export interface VideoAccountView {
  /** Video matches left on the payer's balance (the viewer's own when he pays). */
  credits: number;
  viewerIsPayer: boolean;
  /** Payer's name, for staff who are not the payer. */
  payerName: string | null;
  /** The payer may buy packs now (live Coach, Pro or Club subscription). */
  payerCanBuy: boolean;
  /** First paid day (`VIDEO_PAID_FROM`), null while video stays free. */
  paidFrom: string | null;
  videoPaid: boolean;
  /** With `matchId`: whether this match is covered, and whether its next upload or montage spends a credit. */
  match: { covered: boolean; costsCredit: boolean } | null;
  /** The viewer's own balance and purchases. */
  ownCredits: number;
  canBuy: boolean;
  purchases: VideoPackPurchase[];
}

export const PaymentService = {
  /** Stripe Checkout of a video pack, one-off payment (VOL-332). */
  async createVideoPackCheckout(
    pack: VideoPackSize,
    acceptedCgvVersion: string,
    shown?: ShownConsent,
  ): Promise<{ url: string }> {
    // Only called once both consent boxes are ticked: the waiver is always true here.
    return HttpService.post<{ url: string }>('/payments/video-packs/checkout', {
      pack,
      acceptedCgvVersion,
      waiveWithdrawal: true,
      ...shown,
    });
  },

  async getVideoAccount(matchId?: string): Promise<VideoAccountView> {
    const query = matchId ? `?matchId=${encodeURIComponent(matchId)}` : '';
    return HttpService.get<VideoAccountView>(`/payments/video${query}`);
  },

  /**
   * Crée une session Stripe Checkout et retourne l'URL de paiement.
   *
   * `quantity` = nombre d'équipes pour les plans dégressifs (Coach/Pro).
   * Défaut 1. Le backend applique le barème *graduated pricing* Stripe à cette
   * quantité (cf. §3 — checkout quantité + entitlements dérivés de la quantité).
   */
  async createCheckout(
    plan: PaidPlanId,
    quantity: number,
    acceptedCgvVersion: string,
    shown?: ShownConsent,
  ): Promise<{ url: string }> {
    // Only called once both consent boxes are ticked: the waiver is always true here.
    return HttpService.post<{ url: string }>('/payments/checkout', {
      plan,
      quantity,
      acceptedCgvVersion,
      waiveWithdrawal: true,
      ...shown,
    });
  },

  /** URL du portail client Stripe (gestion / résiliation de l'abonnement). */
  async getPortal(): Promise<{ url: string }> {
    return HttpService.get<{ url: string }>('/payments/portal');
  },

  /**
   * Portal opened on the plan or team count change, once the CGV and the
   * waiver are ticked (VOL-199): the back records them, the portal never asks.
   */
  async createPlanChangePortal(acceptedCgvVersion: string, shown?: ShownConsent): Promise<{ url: string }> {
    return HttpService.post<{ url: string }>('/payments/portal/plan-change', {
      acceptedCgvVersion,
      waiveWithdrawal: true,
      ...shown,
    });
  },

  /** Plan courant de l'utilisateur connecté. */
  async getSubscription(): Promise<SubscriptionView> {
    return HttpService.get<SubscriptionView>('/payments/subscription');
  },

  /**
   * Emails the club owner that the active teams wait for his choice (VOL-199),
   * once a day per club: 409 `CLUB_OWNER_ALREADY_NOTIFIED` after that.
   */
  async notifyClubOwner(): Promise<{ notifiedAt: string }> {
    return HttpService.post<{ notifiedAt: string }>('/payments/club-owner/notify', {});
  },

  async getMatchQuota(teamId?: string): Promise<MatchQuotaView> {
    const query = teamId ? `?teamId=${encodeURIComponent(teamId)}` : '';
    return HttpService.get<MatchQuotaView>(`/payments/match-quota${query}`);
  },
};
